import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { formElementTypes } from "../../../../../utils/Constant";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  getFormSectionsRequest,
  getFormSectionsSuccess,
} from "../../../../../redux/form-templates/slice";
import { useDispatch, useSelector } from "react-redux";
import Loading from "../../../../../components/loading/loading";
import "./preview.css";
import {
  getClientFormSectionsRequest,
  getClientFormSectionsSuccess,
} from "../../../../../redux/clients/slice";
import QuestionTable from "./table";
import MultiInputPreview from "./multi-input";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { RichTextReadOnly } from "mui-tiptap";
import useExtensions from "../../../../../components/text-editor/use-extensions";

export default function Preview({ data }: any) {
  const dispatch = useDispatch();
  const extensions = useExtensions();
  const [sections, setSections] = useState<any[]>([]);
  const [photoRequestSections, setPhotoRequestSections] = useState<any[]>([]);
  const [photoRequestOnly, setPhotoRequestOnly] = useState(false);
  const [isRadioOtherSelected, setIsRadioOtherSelected] = useState(false);
  const [isCheckboxOtherSelected, setIsCheckboxOtherSelected] = useState(false);
  const [canTakePhotos, setCanTakePhotos] = useState(false);

  const { loading, formSections } = useSelector(
    (state: any) => state.formTemplates
  );
  const { loading: clientLoading, formSections: clientFormSections } =
    useSelector((state: any) => state.clients);

  useEffect(() => {
    if (data) {
      if (data?.clientId) {
        dispatch(getClientFormSectionsSuccess({ response: [] }));
        dispatch(getClientFormSectionsRequest({ id: data?.id }));
      } else {
        dispatch(getFormSectionsSuccess({ response: [] }));
        dispatch(getFormSectionsRequest({ id: data?.id }));
      }
    }
  }, [data]); // eslint-disable-line

  useEffect(() => {
    if (data) {
      if (data?.clientId) {
        setSections(clientFormSections);
      } else {
        setSections(formSections);
      }
    }
  }, [data, formSections, clientFormSections]);

  const filterPhotoElements = () => {
    const photoSection: any[] = [];
    sections.forEach((sec) => {
      const elements: any[] = [];
      sec?.elements?.forEach((ele: any) => {
        if (ele?.type === formElementTypes.photoRequest) {
          elements.push(ele);
        }
      });
      if (elements.length > 0) {
        const data = {
          ...sec,
          elements,
        };
        photoSection.push(data);
      }
    });
    setPhotoRequestSections(photoSection);
  };

  return (
    <Box>
      <Box display={"flex"} alignItems={"center"} sx={{ mx: 3, mt: 2 }}>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={photoRequestOnly}
                onChange={(e) => {
                  if (e.target.checked) {
                    filterPhotoElements();
                  }
                  setPhotoRequestOnly(e.target.checked);
                }}
              />
            }
            label="Show Only Photo Requests"
          />
        </FormGroup>
        <Button sx={{ ml: 2 }} variant="outlined" disabled>
          Autofill Responses
        </Button>
      </Box>

      {photoRequestOnly ? (
        <Box sx={{ mx: 3, mt: 2 }}>
          {photoRequestSections.map((section) => (
            <Accordion
              sx={{ backgroundColor: "#FAFAFA", mt: 2 }}
              defaultExpanded
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <Box className="section-header">
                  <Typography variant="h6" fontWeight={"510"}>
                    {section.title}
                  </Typography>
                  {section?.elements && section?.elements?.length > 0 && (
                    <Typography>
                      {section?.elements?.length} Questions
                    </Typography>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ mt: 0.5 }}>
                  {section.introductionText}
                </Typography>
                {section?.elements.map((element: any) => (
                  <Box
                    className={`question-margin ${
                      element.type !== formElementTypes.textField
                        ? "question-layout"
                        : ""
                    }`}
                  >
                    <Box display={"flex"} alignItems={"center"}>
                      <Typography variant="h6" fontWeight={"510"}>
                        {element?.settings?.title}
                      </Typography>
                      {element?.definedName && (
                        <Typography
                          ml={2}
                          sx={{ color: "#2E7D32", fontSize: 14 }}
                        >
                          #{element?.definedName}
                        </Typography>
                      )}
                    </Box>
                    <Typography sx={{ mt: 0.5 }}>
                      {element?.settings?.text}
                    </Typography>
                    {element?.type === formElementTypes.photoRequest && (
                      <>
                        <FormGroup sx={{ mt: 1 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                onChange={(event) =>
                                  setCanTakePhotos(event.target.checked)
                                }
                              />
                            }
                            label="Check if Unable to Take Photos"
                          />
                        </FormGroup>
                        {canTakePhotos && (
                          <TextField
                            sx={{ mt: 1 }}
                            fullWidth
                            placeholder="Value"
                            disabled
                          />
                        )}
                      </>
                    )}
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      ) : (
        <Box sx={{ mx: 3, mt: 2 }}>
          {sections.map((section) => (
            <Accordion
              sx={{ backgroundColor: "#FAFAFA", mt: 2 }}
              defaultExpanded
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <Box className="section-header">
                  <Typography variant="h6" fontWeight={"510"}>
                    {section.title}
                  </Typography>
                  {section?.elements && section?.elements?.length > 0 && (
                    <Typography>
                      {section?.elements?.length} Questions
                    </Typography>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ mt: 0.5 }}>
                  {section.introductionText}
                </Typography>
                {section?.elements.map((element: any) => (
                  <Box
                    className={`question-margin ${
                      element.type !== formElementTypes.textField
                        ? "question-layout"
                        : ""
                    }`}
                  >
                    <Box display={"flex"} alignItems={"center"}>
                      <Typography variant="h6" fontWeight={"510"}>
                        {element?.settings?.title}
                      </Typography>
                      {element?.definedName && (
                        <Typography
                          ml={2}
                          sx={{ color: "#2E7D32", fontSize: 14 }}
                        >
                          #{element?.definedName}
                        </Typography>
                      )}
                    </Box>
                    {element?.type === formElementTypes.textField ? (
                      <RichTextReadOnly
                        content={element?.settings?.text}
                        extensions={extensions}
                      />
                    ) : (
                      <Typography sx={{ mt: 0.5 }}>
                        {element?.settings?.text || ""}
                      </Typography>
                    )}
                    {element?.type === formElementTypes.openEnd && (
                      <TextField
                        sx={{ mt: 1 }}
                        fullWidth
                        placeholder="Value"
                        disabled
                      />
                    )}

                    {element?.type === formElementTypes.singleSelect && (
                      <FormControl sx={{ mt: 1, width: "100%" }}>
                        <RadioGroup name="radio-buttons-group">
                          {element?.settings?.answerOptions.map(
                            (answer: string) => (
                              <FormControlLabel
                                key={answer}
                                value={answer}
                                control={
                                  <Radio
                                    onChange={(event) =>
                                      setIsRadioOtherSelected(false)
                                    }
                                  />
                                }
                                label={answer}
                              />
                            )
                          )}
                          {element?.settings?.includeOtherOption && (
                            <FormControlLabel
                              key="other"
                              value="Other"
                              control={
                                <Radio
                                  onChange={(event) =>
                                    setIsRadioOtherSelected(
                                      event.target.checked
                                    )
                                  }
                                />
                              }
                              label="Other"
                            />
                          )}
                          {isRadioOtherSelected && (
                            <TextField
                              sx={{ mt: 1 }}
                              fullWidth
                              placeholder="Value"
                              disabled
                            />
                          )}
                        </RadioGroup>
                      </FormControl>
                    )}

                    {element?.type === formElementTypes.multiSelect && (
                      <FormControl sx={{ mt: 1, width: "100%" }}>
                        <FormGroup>
                          {element?.settings?.answerOptions.map(
                            (answer: string) => (
                              <FormControlLabel
                                key={answer}
                                value={answer}
                                control={<Checkbox />}
                                label={answer}
                              />
                            )
                          )}
                          {element?.settings?.includeOtherOption && (
                            <FormControlLabel
                              key="other"
                              value="Other"
                              control={
                                <Checkbox
                                  onChange={(event) =>
                                    setIsCheckboxOtherSelected(
                                      event.target.checked
                                    )
                                  }
                                />
                              }
                              label="Other"
                            />
                          )}
                          {isCheckboxOtherSelected && (
                            <TextField
                              sx={{ mt: 1 }}
                              fullWidth
                              placeholder="Value"
                              disabled
                            />
                          )}
                        </FormGroup>
                      </FormControl>
                    )}

                    {element?.type === formElementTypes.matrix && (
                      <Box sx={{ borderLeft: "1px solid #C4C4C4", mt: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Typography
                            className="question-matrix-header-text"
                            sx={{ width: 150, minWidth: 150 }}
                          ></Typography>
                          {element?.settings?.answerOptions?.map(
                            (option: string) => (
                              <Typography
                                key={option}
                                className="question-matrix-header-text"
                              >
                                {option}
                              </Typography>
                            )
                          )}
                        </Box>
                        {element?.childElements?.map(
                          (question: any, index: number) => (
                            <Box
                              key={index}
                              sx={{ display: "flex", alignItems: "center" }}
                            >
                              <Box
                                className="question-matrix-subquestions-layout"
                                sx={{ width: 150 }}
                              >
                                <Typography className="question-matrix-subquestion-txt">
                                  {question.title}
                                </Typography>
                                <Typography className="question-matrix-subquestion-defined-name">
                                  #{question.definedName}
                                </Typography>
                              </Box>
                              {element?.settings?.answerOptions?.map(
                                (Option: string, index: number) => (
                                  <Box
                                    key={index}
                                    className="question-matrix-subquestion-radio"
                                  >
                                    <Radio disabled />
                                  </Box>
                                )
                              )}
                            </Box>
                          )
                        )}
                      </Box>
                    )}

                    {element?.type === formElementTypes.table && (
                      <QuestionTable element={element} disabled={true} />
                    )}

                    {element?.type === formElementTypes.multiInput && (
                      <MultiInputPreview element={element} disabled={true} />
                    )}

                    {element?.type === formElementTypes.singleDate && (
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker sx={{ width: 300, mt: 1 }} format="LL" />
                      </LocalizationProvider>
                    )}

                    {element?.type === formElementTypes.photoRequest && (
                      <>
                        <FormGroup sx={{ mt: 1 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                onChange={(event) =>
                                  setCanTakePhotos(event.target.checked)
                                }
                              />
                            }
                            label="Check if Unable to Take Photos"
                          />
                        </FormGroup>
                        {canTakePhotos && (
                          <TextField
                            sx={{ mt: 1 }}
                            fullWidth
                            placeholder="Value"
                            disabled
                          />
                        )}
                      </>
                    )}
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {(loading || clientLoading) && <Loading />}
    </Box>
  );
}
